import _flatMap from 'lodash/flatMap';
import _fromPairs from 'lodash/fromPairs';
import _isEmpty from 'lodash/isEmpty';
import _isObjectLike from 'lodash/isObjectLike';
import _mapValues from 'lodash/mapValues';
import _uniqBy from 'lodash/uniqBy';
import _values from 'lodash/values';
import { createReducer } from 'redux-act';
import { createSelector, createSelectorCreator, createStructuredSelector, lruMemoize } from 'reselect';
import shallowequal from 'shallowequal';
import { createResource } from '@gandi/resource/redux';
import { hashString } from '../utils';
export const baseUrl = '/contactbook/contactbook';
export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 10;
export const DEFAULT_SORT_BY = 'email';
export const defaultParams = {
  page: DEFAULT_PAGE,
  per_page: DEFAULT_PER_PAGE,
  sort_by: DEFAULT_SORT_BY
};
export const computeListLabel = (orgId, params) => `${orgId}/list-${hashString(JSON.stringify(params))}`;
export const computeContactsWithRoleLabel = orgId => `${orgId}/withRole`;
export const computeContactLabel = (orgId, contactId, tld = '', role = '') => `${orgId}/${contactId}/${tld}/${role}`;
export const defaultContactValue = {
  id: '',
  type: 'individual',
  roles: [],
  given: '',
  family: '',
  orgname: '',
  email: '',
  streetaddr: '',
  streetaddr2: '',
  city: '',
  zip: '',
  country: '',
  phone: '',
  fax: '',
  mail_obfuscated: true,
  data_obfuscated: true
};
export const resourceName = 'resources-contactbook.contactbook';
export const resource = createResource(resourceName);
export const resourceWithRoleName = 'resources-contactbook.contactbookWithRole';
export const resourceWithRole = createResource(resourceWithRoleName);
export const resourceContactExtraParamsName = 'resources-contactbook.contactExtraParams';
export const resourceContactExtraParams = createResource(resourceContactExtraParamsName);
export const fetchContactList = (orgId, fetchParams, options = {}) => dispatch => {
  const mergedParams = {
    ...defaultParams,
    ...fetchParams
  };
  const label = computeListLabel(orgId, fetchParams);
  return dispatch(resource.fetch(`/contactbook/contactbook/${orgId}`, {
    label,
    params: mergedParams,
    props: {
      orgId
    },
    on200: response => response,
    not200: response => Promise.reject(response),
    ...options
  }));
};
export const fetchContactsWithRole = (orgId, options = {}) => dispatch => dispatch(resourceWithRole.fetch(`/contactbook/contactbook/${orgId}/roles`, {
  label: computeContactsWithRoleLabel(orgId),
  props: {
    orgId
  },
  on200: response => response,
  not200: response => Promise.reject(response),
  ...options
}));
export const fetchContact = (orgId, contactId, options = {}) => dispatch => dispatch(resource.fetchOne(`/contactbook/contactbook/${orgId}/${contactId}`, {
  label: computeContactLabel(orgId, contactId),
  props: {
    orgId,
    contactId
  },
  on200: response => response,
  not200: response => Promise.reject(response),
  ...options
}));
export const createContact = (orgId, data, options = {}) => dispatch => dispatch(resource.createOne(`/contactbook/contactbook/${orgId}`, data, {
  label: computeContactLabel(orgId, 'new'),
  props: {
    orgId,
    data
  },
  on201: response => response,
  not201: response => Promise.reject(response),
  ...options
}));
export const editContact = (orgId, contactId, data, options = {}) => dispatch => dispatch(resource.patchOne(`/contactbook/contactbook/${orgId}/${contactId}`, data, {
  label: computeContactLabel(orgId, contactId),
  props: {
    orgId,
    contactId,
    data
  },
  on202: response => response,
  not202: response => Promise.reject(response),
  ...options
}));
export const deleteContact = (orgId, contactId, options = {}) => dispatch => dispatch(resource.deleteOne(`/contactbook/contactbook/${orgId}/${contactId}`, {
  label: computeContactLabel(orgId, contactId),
  props: {
    orgId,
    contactId
  },
  on204: response => response,
  not204: response => Promise.reject(response),
  ...options
}));
export const invalidate = () => dispatch => dispatch(resource.invalidate());
export const fetchContactExtraParams = (orgId, contactId, tld, options = {}) => dispatch => dispatch(resourceContactExtraParams.fetchOne(`/contactbook/contactbook/${orgId}/${contactId}/tlds${tld ? `/${tld}` : ''}`, {
  label: computeContactLabel(orgId, contactId, tld),
  props: {
    orgId,
    contactId,
    tld
  },
  on200: response => response,
  not200: response => Promise.reject(response),
  ...options
}));
export const fetchOwnerContactExtraParams = (orgId, tld, options = {}) => dispatch => dispatch(resourceContactExtraParams.fetchOne(`/contactbook/contactbook/${orgId}/tlds${tld ? `/${tld}` : ''}`, {
  label: computeContactLabel(orgId, 'owner', tld),
  props: {
    orgId,
    contactId: orgId,
    tld
  },
  on200: response => response,
  not200: response => Promise.reject(response),
  ...options
}));
export const editContactExtraParams = (orgId, contactId, tld, role, extraParams, options = {}) => dispatch => dispatch(resourceContactExtraParams.put(`/contactbook/contactbook/${orgId}/${contactId}/tlds/${tld}/${role}`, extraParams, {
  label: computeContactLabel(orgId, contactId, tld, role),
  props: {
    orgId,
    contactId,
    tld,
    role,
    extraParams
  },
  on202: response => response,
  not202: response => Promise.reject(response),
  ...options
}));
export const editOwnerContactExtraParams = (orgId, tld, role, extraParams, options = {}) => dispatch => dispatch(resourceContactExtraParams.put(`/contactbook/contactbook/${orgId}/tlds/${tld}/${role}`, extraParams, {
  label: computeContactLabel(orgId, 'owner', tld, role),
  props: {
    orgId,
    contactId: orgId,
    tld,
    role,
    extraParams
  },
  on202: response => response,
  not202: response => Promise.reject(response),
  ...options
}));
export const deleteContactExtraParams = (orgId, contactId, tld, role, options = {}) => dispatch => dispatch(resourceContactExtraParams.deleteOne(`/contactbook/contactbook/${orgId}/${contactId}/tlds/${tld}/${role}`, {
  label: computeContactLabel(orgId, contactId, tld, role),
  props: {
    orgId,
    contactId,
    tld,
    role
  },
  on204: response => response,
  not204: response => Promise.reject(response),
  ...options
}));
export const deleteOwnerContactExtraParams = (orgId, tld, role, options = {}) => dispatch => dispatch(resourceContactExtraParams.deleteOne(`/contactbook/contactbook/${orgId}/tlds/${tld}/${role}`, {
  label: computeContactLabel(orgId, 'owner', tld, role),
  props: {
    orgId,
    contactId: orgId,
    tld,
    role
  },
  on204: response => response,
  not204: response => Promise.reject(response),
  ...options
}));
export const invalidateContactExtraParams = () => dispatch => dispatch(resourceContactExtraParams.invalidate());
export const initialState = {};
export const orgContactBookInitialState = {
  lists: {},
  contacts: {},
  contactsExtraParams: {},
  totalCount: 0
};
const fetchContactListHandler = (state, {
  response,
  label,
  props: {
    orgId
  }
}) => ({
  ...state,
  [orgId]: {
    ...(state[orgId] || orgContactBookInitialState),
    lists: {
      ...(state[orgId]?.lists || {}),
      [label]: {
        contactIds: response.data.map(contact => contact.id),
        filteredCount: response.headers.get('x-filtered-count')
      }
    },
    contacts: {
      ...(state[orgId]?.contacts || {}),
      ..._fromPairs(response.data.map(contact => [contact.id, {
        ...defaultContactValue,
        ...contact
      }]))
    },
    totalCount: parseInt(response.headers.get('x-total-count'), 10)
  }
});
const fetchContactsWithRoleHandler = (state, {
  response,
  props: {
    orgId
  }
}) => {
  const newContacts = _uniqBy(_values(response.data), contact => contact?.id ?? null).filter(Boolean);
  return {
    ...state,
    [orgId]: {
      ...(state[orgId] || orgContactBookInitialState),
      contacts: {
        ...(state[orgId]?.contacts || {}),
        ..._fromPairs(newContacts.map(contact => [contact.id, {
          ...defaultContactValue,
          ...contact
        }]))
      }
    }
  };
};
const fetchContactHandler = (state, {
  response,
  props: {
    orgId,
    contactId
  }
}) => ({
  ...state,
  [orgId]: {
    ...(state[orgId] || orgContactBookInitialState),
    contacts: {
      ...(state[orgId]?.contacts || {}),
      [contactId]: {
        ...defaultContactValue,
        ...response.data
      }
    }
  }
});
const editContactHandler = (state, {
  props: {
    orgId,
    contactId,
    data
  }
}) => ({
  ...state,
  [orgId]: {
    ...(state[orgId] || orgContactBookInitialState),
    contacts: {
      ...(state[orgId]?.contacts || {}),
      [contactId]: {
        ...(state[orgId]?.contacts?.[contactId] || {}),
        ...data
      }
    }
  }
});
const deleteContactHandler = (state, {
  props: {
    orgId,
    contactId
  }
}) => {
  const {
    contacts: {
      [contactId]: deletedContact,
      ...remainingContacts
    } = {},
    lists,
    ...remainingState
  } = state[orgId] || orgContactBookInitialState;
  return {
    ...state,
    [orgId]: {
      lists: _mapValues(lists, ({
        contactIds,
        ...etc
      }) => ({
        contactIds: contactIds.filter(id => id !== contactId),
        ...etc
      })),
      contacts: remainingContacts,
      ...remainingState
    }
  };
};
const fetchContactExtraParamsHandler = (state, {
  response,
  props: {
    orgId,
    contactId,
    tld
  }
}) => ({
  ...state,
  [orgId]: {
    ...(state[orgId] || orgContactBookInitialState),
    contactsExtraParams: {
      ...(state[orgId]?.contactsExtraParams || {}),
      [contactId]: {
        ...(tld && state[orgId]?.contactsExtraParams?.[contactId] || {}),
        ...response.data
      }
    }
  }
});
const editContactExtraParamsHandler = (state, {
  props: {
    orgId,
    contactId,
    tld,
    role,
    extraParams
  }
}) => ({
  ...state,
  [orgId]: {
    ...(state[orgId] || orgContactBookInitialState),
    contactsExtraParams: {
      ...(state[orgId]?.contactsExtraParams || {}),
      [contactId]: {
        ...(state[orgId]?.contactsExtraParams?.[contactId] || {}),
        [tld]: {
          ...(state[orgId]?.contactsExtraParams?.[contactId]?.[tld] || {}),
          [role]: extraParams
        }
      }
    }
  }
});
const deleteContactExtraParamsHandler = (state, {
  props: {
    orgId,
    contactId,
    tld,
    role
  }
}) => ({
  ...state,
  [orgId]: {
    ...(state[orgId] || orgContactBookInitialState),
    contactsExtraParams: {
      ...(state[orgId]?.contactsExtraParams || {}),
      [contactId]: _fromPairs(Object.entries({
        ...(state[orgId]?.contactsExtraParams?.[contactId] || {}),
        [tld]: _fromPairs(Object.entries(state[orgId]?.contactsExtraParams?.[contactId]?.[tld] || {}).filter(entry => entry[0] !== role))
      }).filter(([, extraParamsByRole]) => !_isEmpty(extraParamsByRole)))
    }
  }
});
export const reducer = createReducer({
  [resource.fetch.success.getType()]: fetchContactListHandler,
  [resourceWithRole.fetch.success.getType()]: fetchContactsWithRoleHandler,
  [resource.fetchOne.success.getType()]: fetchContactHandler,
  [resource.patchOne.success.getType()]: editContactHandler,
  [resource.deleteOne.success.getType()]: deleteContactHandler,
  [resourceContactExtraParams.fetchOne.success.getType()]: fetchContactExtraParamsHandler,
  [resourceContactExtraParams.put.success.getType()]: editContactExtraParamsHandler,
  [resourceContactExtraParams.deleteOne.success.getType()]: deleteContactExtraParamsHandler
}, initialState);
const selectRawState = state => state.gandiReduxResourceContactBook.contactbook || {};
const EMPTY_CONTACT_LIST = {
  contactIds: [],
  filteredCount: 0
};
const EMPTY_OBJECT = {};
export const selectRawContactList = createSelector([selectRawState, (_, orgId) => orgId], (raw, orgId) => raw[orgId]?.lists || EMPTY_OBJECT);
const createShallowEqualSelector = createSelectorCreator(lruMemoize, shallowequal);
export const selectComputedListLabel = createShallowEqualSelector([(_, orgId, params) => computeListLabel(orgId, params)], label => label);
export const selectComputedContactsWithRoleLabel = createShallowEqualSelector([(_, orgId) => computeContactsWithRoleLabel(orgId)], label => label);
export const selectContactList = createSelector([selectRawContactList, selectComputedListLabel], (raw, label) => raw[label] || EMPTY_CONTACT_LIST);
export const selectContactsById = createSelector([selectRawState, (_, orgId) => orgId], (raw, orgId) => raw[orgId]?.contacts || EMPTY_OBJECT);
export const selectContactsTotalCount = createSelector([selectRawState, (_, orgId) => orgId], (raw, orgId) => raw[orgId]?.totalCount || 0);
export const selectContactByRole = createShallowEqualSelector(selectContactsById, contactsById => _fromPairs(_flatMap(Object.values(contactsById), contact => {
  if (_isObjectLike(contact) && Array.isArray(contact?.roles)) {
    return contact.roles.map(role => [role, contact]);
  }
  return [];
})));
export const selectContactsFetchProps = createStructuredSelector({
  page: ({
    page
  }) => parseInt(page ?? defaultParams.page, 10),
  per_page: ({
    per_page: perPage
  }) => parseInt(perPage ?? defaultParams.per_page, 10),
  sort_by: ({
    sort_by: sortBy
  }) => sortBy ?? defaultParams.sort_by,
  email: ({
    email
  }) => email,
  '~email': ({
    '~email': likeEmail
  }) => likeEmail
});
export const selectContactsExtraParams = (state, orgId) => selectRawState(state)[orgId]?.contactsExtraParams;
export const selectContactExtraParams = (state, orgId, contactId) => selectRawState(state)[orgId]?.contactsExtraParams?.[contactId];
export const selectOwnerContactExtraParams = (state, orgId) => selectRawState(state)[orgId]?.contactsExtraParams?.[orgId];
export const selectIsFetching = (state, orgId, params) => resource.selectIsFetching(state, selectComputedListLabel(state, orgId, params));
export const selectIsLoaded = (state, orgId, params) => resource.selectIsLoaded(state, selectComputedListLabel(state, orgId, params));
export const selectIsFailure = (state, orgId, params) => resource.selectIsFailure(state, selectComputedListLabel(state, orgId, params));
export const selectIsForbidden = (state, orgId, params) => resource.selectIsFailure(state, {
  label: selectComputedListLabel(state, orgId, params),
  status: 403
});
export const selectIsToRefetch = (state, orgId, params) => resource.selectIsToRefetch(state, selectComputedListLabel(state, orgId, params));
export const selectContactsWithRoleIsFetching = (state, orgId) => resourceWithRole.selectIsFetching(state, selectComputedContactsWithRoleLabel(state, orgId));
export const selectContactsWithRoleIsLoaded = (state, orgId) => resourceWithRole.selectIsLoaded(state, selectComputedContactsWithRoleLabel(state, orgId));
export const selectContactsWithRoleIsFailure = (state, orgId) => resourceWithRole.selectIsFailure(state, selectComputedContactsWithRoleLabel(state, orgId));
export const selectContactsWithRoleIsForbidden = (state, orgId) => resourceWithRole.selectIsFailure(state, {
  label: selectComputedContactsWithRoleLabel(state, orgId),
  status: 403
});
export const selectContactsWithRoleIsToRefetch = (state, orgId) => resourceWithRole.selectIsToRefetch(state, selectComputedContactsWithRoleLabel(state, orgId));
export const selectIsFetchingOne = (state, orgId, contactId) => resource.selectIsFetchingOne(state, computeContactLabel(orgId, contactId));
export const selectIsLoadedOne = (state, orgId, contactId) => resource.selectIsLoadedOne(state, computeContactLabel(orgId, contactId));
export const selectIsFailureOne = (state, orgId, contactId) => resource.selectIsFailureOne(state, computeContactLabel(orgId, contactId));
export const selectIsToRefetchOne = (state, orgId, contactId) => resource.selectIsToRefetchOne(state, computeContactLabel(orgId, contactId));
export const selectIsCreatingOne = (state, orgId) => resource.selectIsCreatingOne(state, computeContactLabel(orgId, 'new'));
export const selectIsPatchingOne = (state, orgId, contactId) => resource.selectIsPatchingOne(state, computeContactLabel(orgId, contactId));
export const selectIsDeletingOne = (state, orgId, contactId) => resource.selectIsDeletingOne(state, computeContactLabel(orgId, contactId));
export const selectIsFetchingOneContactExtraParams = (state, orgId, contactId, tld) => resourceContactExtraParams.selectIsFetchingOne(state, computeContactLabel(orgId, contactId, tld));
export const selectIsLoadedOneContactExtraParams = (state, orgId, contactId, tld) => resourceContactExtraParams.selectIsLoadedOne(state, computeContactLabel(orgId, contactId, tld));
export const selectIsFailureOneContactExtraParams = (state, orgId, contactId, tld) => resourceContactExtraParams.selectIsFailureOne(state, computeContactLabel(orgId, contactId, tld));
export const selectIsToRefetchOneContactExtraParams = (state, orgId, contactId, tld) => resourceContactExtraParams.selectIsToRefetchOne(state, computeContactLabel(orgId, contactId, tld));
export const selectIsPuttingContactExtraParams = (state, orgId, contactId, tld, role) => resourceContactExtraParams.selectIsPutting(state, computeContactLabel(orgId, contactId, tld, role));
export const selectIsDeletingOneContactExtraParams = (state, orgId, contactId, tld, role) => resourceContactExtraParams.selectIsDeletingOne(state, computeContactLabel(orgId, contactId, tld, role));
export const selectIsFetchingOwnerContactExtraParams = (state, orgId, tld) => resourceContactExtraParams.selectIsFetchingOne(state, computeContactLabel(orgId, 'owner', tld));
export const selectIsLoadedOwnerContactExtraParams = (state, orgId, tld) => resourceContactExtraParams.selectIsLoadedOne(state, computeContactLabel(orgId, 'owner', tld));
export const selectIsFailureOwnerContactExtraParams = (state, orgId, tld) => resourceContactExtraParams.selectIsFailureOne(state, computeContactLabel(orgId, 'owner', tld));
export const selectIsToRefetchOwnerContactExtraParams = (state, orgId, tld) => resourceContactExtraParams.selectIsToRefetchOne(state, computeContactLabel(orgId, 'owner', tld));